import { Sparkles, Check, ArrowUpRight } from 'lucide-react';
import { Button } from '../ui';
import type { ProposedActionUI } from './use-assistant-chat';

export const TOOL_LABEL: Record<string, string> = {
  // read tools
  explain_client_risk: 'riesgo',
  list_missing_documents: 'documentos',
  screen_name: 'listas',
  find_client: 'clientes',
  list_clients: 'clientes',
  explain_risk_matrix: 'matriz de riesgo',
  get_dashboard_summary: 'panel',
  list_alerts: 'alertas',
  // write tools (surfaced as confirm cards)
  create_expediente: 'nuevo expediente',
  update_expediente: 'actualizar expediente',
  request_documents: 'solicitar documentos',
  update_alert: 'actualizar alerta',
  run_validation: 'screening',
  create_review: 'programar revisión',
  update_risk_matrix: 'actualizar matriz de riesgo',
  update_indicator_bands: 'ajustar indicadores',
  update_entity_mode: 'cambiar tipo de entidad',
};

/**
 * Map an execute-result entity to an in-app route (HashRouter path).
 *
 * Backend entity types: `expediente`, `alert`, `document_request`,
 * `scheduled_review`, `validation_run`. For `expediente`/`alert` the `entity.id`
 * IS the routable row; for the other three `entity.id` is the child row's id
 * (not the expediente), so we route to the parent expediente via the action's
 * forwarded `input.expedienteId`. No expedienteId → no link.
 */
function resolveEntityHref(
  entity: { type: string; id: string },
  input: Record<string, unknown>
): string | null {
  const expId = typeof input.expedienteId === 'string' ? input.expedienteId : null;
  switch (entity.type) {
    case 'expediente':
      return `/revision/${entity.id}`;
    case 'alert':
      return `/alertas/${entity.id}`;
    case 'document_request':
    case 'scheduled_review':
    case 'validation_run':
      return expId ? `/revision/${expId}` : null;
    default:
      return null;
  }
}

/** One copilot-proposed write action, rendered as a review-then-confirm card. */
export function ActionCard({
  action,
  labels,
  onConfirm,
  onCancel,
  onOpen,
}: {
  action: ProposedActionUI;
  labels: {
    proposedAction: string;
    confirm: string;
    cancel: string;
    executed: string;
    cancelled: string;
    reviewBeforeConfirm: string;
  };
  onConfirm: () => void;
  onCancel: () => void;
  onOpen: (href: string) => void;
}) {
  // Cancelled proposals collapse to a single muted line.
  if (action.state === 'cancelled') {
    return (
      <div className="w-full max-w-[90%] rounded-xl border border-line bg-canvas px-3 py-2 text-xs text-ink-faint">
        {labels.cancelled} · {action.title}
      </div>
    );
  }

  const executing = action.state === 'executing';
  const executed = action.state === 'executed';
  const link = action.entity ? resolveEntityHref(action.entity, action.input) : null;

  return (
    <div className="w-full max-w-[90%] rounded-2xl border border-line-strong bg-panel p-4 shadow-sm">
      <div className="flex items-center gap-2">
        <span className="grid h-6 w-6 place-items-center rounded-lg bg-primary/10 text-primary-dark">
          <Sparkles className="h-3.5 w-3.5" strokeWidth={2} />
        </span>
        <span className="font-mono text-[10px] uppercase tracking-wider text-primary-dark">
          {labels.proposedAction} · {TOOL_LABEL[action.tool] ?? action.tool}
        </span>
      </div>
      <div className="mt-2 font-serif text-sm font-medium text-ink">{action.title}</div>
      <p className="mt-1 text-xs text-ink-soft">{action.summary}</p>

      {executed ? (
        <>
          <div className="mt-3 flex items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-semibold text-primary-dark motion-safe:animate-pop">
              <Check className="h-3.5 w-3.5" strokeWidth={2.5} /> {labels.executed}
            </span>
            {link && (
              <button
                onClick={() => onOpen(link)}
                className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-primary-dark hover:underline"
              >
                Ver <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2.5} />
              </button>
            )}
          </div>
          {/* connector trail — reveals in sequence like the MCP call log in the demo reel */}
          <div className="mt-2.5 space-y-0.5 border-t border-line pt-2">
            <div
              className="font-mono text-[10px] text-ink-faint motion-safe:animate-slide-up"
              style={{ animationDelay: '120ms', animationFillMode: 'both' }}
            >
              conector · {TOOL_LABEL[action.tool] ?? action.tool} · ejecutado
            </div>
            <div
              className="font-mono text-[10px] text-ink-faint motion-safe:animate-slide-up"
              style={{ animationDelay: '240ms', animationFillMode: 'both' }}
            >
              Quedó en la bitácora como acción confirmada por usted
            </div>
          </div>
        </>
      ) : action.expired ? (
        <p className="mt-3 rounded-lg bg-canvas px-3 py-2 text-xs text-ink-faint">
          Esta propuesta expiró. Vuelve a pedírmela para ejecutarla.
        </p>
      ) : (
        <>
          <p className="mt-3 rounded-lg bg-canvas px-3 py-2 text-xs text-ink-soft">
            {labels.reviewBeforeConfirm}
          </p>
          {action.state === 'error' && action.errorMessage && (
            <p className="mt-2 text-xs font-medium text-tone-red">{action.errorMessage}</p>
          )}
          <div className="mt-3 flex gap-2">
            <Button onClick={onConfirm} disabled={executing} className="px-3 py-2 text-xs">
              {executing ? (
                <>
                  <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  Ejecutando…
                </>
              ) : (
                labels.confirm
              )}
            </Button>
            <Button variant="ghost" onClick={onCancel} disabled={executing} className="px-3 py-2 text-xs">
              {labels.cancel}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
